import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { getSessionToken } from "../utils/sessionAuth";
import { getVoiceAgentUrl } from "../utils/backend";
import { getSessionUserId } from "../utils/sessionUser";

type Props = {
  children: ReactNode | ((controls: RenderProps) => ReactNode);
  wsUrl?: string;
  endpoint?: string;
  userId?: string;
  instanceId?: string;
  onStatus?: (status: string) => void;
  onError?: (error: string) => void;
  onSessionReady?: (sessionId: string) => void;
  className?: string;
};

type ConnectionState = "DISCONNECTED" | "CONNECTING" | "CONNECTED";
type RenderProps = {
  status: string;
  isRecording: boolean;
  isLoading: boolean;
  isConnected: ConnectionState;
  startRecording: () => void;
  stopRecording: () => void;
};

const appendQueryParam = (url: string, key: string, value: string) => {
  const [base, hash] = url.split("#");
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}${hash ? `#${hash}` : ""}`;
};

function downsampleTo24k(input: Float32Array, inputSampleRate: number) {
  if (inputSampleRate === 24000) return input;
  const ratio = inputSampleRate / 24000;
  const length = Math.round(input.length / ratio);
  const out = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const idx = i * ratio;
    const idxLow = Math.floor(idx);
    const idxHigh = Math.min(Math.ceil(idx), input.length - 1);
    const weight = idx - idxLow;
    out[i] = (1 - weight) * input[idxLow] + weight * input[idxHigh];
  }
  return out;
}

function floatToPcm16(input: Float32Array) {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/**
 * Wrap this component around a button. When clicked, it will:
 *  - open a websocket to the backend `/listen`
 *  - stream microphone audio as base64 PCM16 to the server
 *  - play back response audio deltas from the server
 * Click again to stop/cleanup.
 */
export function AudioCaptureWrapper({
  children,
  wsUrl,
  endpoint = "/listen",
  userId,
  instanceId,
  onStatus,
  onError,
  onSessionReady,
  className,
}: Props) {
  const [status, setStatus] = useState("Ready to record");
  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>("DISCONNECTED");
  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const playbackTimeRef = useRef(0);
  const scheduledSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const playbackLeadSeconds = 0.12;
  const fadeSeconds = 0.01;

  const emitStatus = useCallback(
    (s: string) => {
      setStatus(s);
      onStatus?.(s);
      console.log("[VoiceLiveButton]", s);
    },
    [onStatus],
  );

  const emitError = useCallback(
    (e: string) => {
      onError?.(e);
      console.error("[VoiceLiveButton] error:", e);
    },
    [onError],
  );

  const buildWsUrl = useCallback(() => {
    const envWs = import.meta.env.VITE_WS_URL as string | undefined;
    if (envWs) return `${envWs.replace(/\/$/, "")}${endpoint}`;
    const voiceUrl = getVoiceAgentUrl();
    if (voiceUrl) return `${voiceUrl}${endpoint}`;
    const loc = window.location;
    const proto = loc.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${loc.host}${endpoint}`;
  }, [endpoint]);

  const stopPlayback = useCallback((reason?: string) => {
    playbackTimeRef.current = 0;
    scheduledSourcesRef.current.forEach((src) => {
      try {
        src.onended = null;
        src.stop();
      } catch {
        // Best effort only.
      }
    });
    scheduledSourcesRef.current = [];
    const src = currentSourceRef.current;
    if (src) {
      try {
        src.onended = null;
        src.stop();
      } catch {
        // Best effort only.
      }
      currentSourceRef.current = null;
    }
    void reason;
  }, []);

  const cleanUp = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;

    processorRef.current?.disconnect();
    processorRef.current = null;

    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;

    audioCtxRef.current?.close().catch(() => undefined);
    audioCtxRef.current = null;

    stopPlayback("cleanup");

    setIsActive(false);
    setIsConnecting(false);
    setConnectionState("DISCONNECTED");
    emitStatus("stopped");
  }, [emitStatus, stopPlayback]);

  useEffect(() => cleanUp, [cleanUp]);

  const dispatchStopOthers = useCallback(() => {
    if (instanceId) {
      window.dispatchEvent(new CustomEvent("stop-other-voice-agents", {
        detail: { instanceId }
      }));
    }
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) return;
    
    const handleStopOthers = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.instanceId !== instanceId) {
        cleanUp();
      }
    };
    
    window.addEventListener("stop-other-voice-agents", handleStopOthers);
    return () => window.removeEventListener("stop-other-voice-agents", handleStopOthers);
  }, [instanceId, cleanUp]);

  const enqueuePlayback = useCallback(
    (pcm16: ArrayBuffer) => {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      const view = new Int16Array(pcm16);
      const audioBuffer = ctx.createBuffer(1, view.length, 24000);
      const channel = audioBuffer.getChannelData(0);
      for (let i = 0; i < view.length; i += 1) {
        channel[i] = view[i] / 32768;
      }
      const duration = audioBuffer.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const now = ctx.currentTime;
      if (playbackTimeRef.current < now) {
        playbackTimeRef.current = now;
      }
      const startTime = Math.max(now + playbackLeadSeconds, playbackTimeRef.current);
      const src = ctx.createBufferSource();
      const gain = ctx.createGain();
      src.buffer = audioBuffer;
      src.connect(gain);
      gain.connect(ctx.destination);

      const fade = Math.min(fadeSeconds, duration / 2);
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(1, startTime + fade);
      gain.gain.setValueAtTime(1, startTime + duration - fade);
      gain.gain.linearRampToValueAtTime(0, startTime + duration);

      scheduledSourcesRef.current.push(src);
      currentSourceRef.current = src;
      src.onended = () => {
        scheduledSourcesRef.current = scheduledSourcesRef.current.filter((s) => s !== src);
        if (currentSourceRef.current === src) currentSourceRef.current = null;
      };
      src.start(startTime);
      playbackTimeRef.current = startTime + duration;
    },
    [fadeSeconds, playbackLeadSeconds],
  );


  const handleWsMessage = useCallback(
    async (ev: MessageEvent) => {
      try {
        const payload = JSON.parse(ev.data);
        const t = payload.type;
        if (t === "speech_started" || t === "response_interrupt") {
          stopPlayback(t);
          return;
        }
        if (t === "session_ready") {
          onSessionReady?.(payload.session_id);
          emitStatus("session_ready");
        } else if (t === "status") {
          emitStatus(payload.message);
        } else if (t === "error") {
          emitError(payload.message || "Unknown error");
        } else if (t === "response_audio_delta" && payload.audio) {
          const pcm = Uint8Array.from(atob(payload.audio), (c) => c.charCodeAt(0)).buffer;
          enqueuePlayback(pcm);
        }
      } catch (err) {
        emitError((err as Error).message);
      }
    },
    [emitError, emitStatus, enqueuePlayback, onSessionReady, stopPlayback],
  );

  const start = useCallback(async () => {
    if (isActive || isConnecting) return;
    dispatchStopOthers();
    setIsConnecting(true);
    setConnectionState("CONNECTING");
    emitStatus("requesting_microphone");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new AudioContext();
      mediaStreamRef.current = stream;
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      const baseUrl = wsUrl || buildWsUrl();
      const token = getSessionToken();
      const uId = userId || getSessionUserId();
      let socketUrl = baseUrl;
      if (uId) {
        socketUrl = appendQueryParam(socketUrl, "id", uId);
      }
      if (token) {
        socketUrl = appendQueryParam(socketUrl, "token", token);
      }
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        emitStatus("connected");
        setIsConnecting(false);
        setConnectionState("CONNECTED");
        setIsActive(true);
        // Onboarding agent expects token in query string only. Sending auth message might confuse it.
        const isOnboarding = socketUrl.includes("mode=onboarding");
        if (token && !isOnboarding) {
          ws.send(JSON.stringify({ type: "auth", scheme: "Bearer", token }));
        }
      };
      ws.onclose = () => {
        emitStatus("ws_closed");
        cleanUp();
      };
      ws.onerror = () => {
        emitError("WebSocket error");
        cleanUp();
      };
      ws.onmessage = handleWsMessage;

      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const down = downsampleTo24k(input, audioCtx.sampleRate);
        const pcm16 = floatToPcm16(down);
        const b64 = btoa(
          String.fromCharCode.apply(null, Array.from(new Uint8Array(pcm16.buffer)) as number[]),
        );
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "audio", data: b64 }));
        }
      };

      source.connect(processor);
      processor.connect(audioCtx.destination);
    } catch (err) {
      emitError(`Setup failed: ${(err as Error).message}`);
      cleanUp();
    }
  }, [buildWsUrl, cleanUp, dispatchStopOthers, downsampleTo24k, emitError, emitStatus, floatToPcm16, handleWsMessage, isActive, isConnecting, wsUrl, userId]);

  const stop = useCallback(() => cleanUp(), [cleanUp]);

  useEffect(() => {
    const handleToggle = () => {
      if (isActive) stop();
      else void start();
    };
    window.addEventListener("toggle-voice-agent", handleToggle);
    return () => window.removeEventListener("toggle-voice-agent", handleToggle);
  }, [isActive, start, stop]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (typeof children === "function") return;
      if (isActive) stop();
      else void start();
    },
    [children, isActive, start, stop],
  );

  const renderChildren =
    typeof children === "function"
      ? children({
          status,
          isRecording: isActive,
          isLoading: isConnecting || connectionState === "CONNECTING",
          isConnected: connectionState,
          startRecording: start,
          stopRecording: stop,
        })
      : children;

  return (
    <span
      onClick={handleClick}
      className={className}
      role={typeof children === "function" ? undefined : "button"}
      aria-pressed={typeof children === "function" ? undefined : isActive}
    >
      {renderChildren}
    </span>
  );
}

export default AudioCaptureWrapper;
